/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { Icon, TextField, Button, FlexRow, FlexCol, Select } from 'sarsaparilla';
import get from 'lodash/get';
import {
    ADULT_YOUTH_PASS,
    ADULT_PASS,
    YOUTH_PASS,
    NON_COMMERCIAL_PASS,
} from '../../constants/shared-constants';

export default class PassInfo extends React.Component {
    static propTypes = {
        index: PropTypes.number.isRequired,
        handleMemberInfoChange: PropTypes.func.isRequired,
        member: PropTypes.shape({
            passholderId: PropTypes.string,
            passholderType: PropTypes.number,
        }).isRequired,
        options: PropTypes.object,
        alwaysDisplayPassOptions: PropTypes.bool,
        formatPackCreekFeeDescription: PropTypes.func,
    };
    constructor() {
        super();

        this.togglePass = this.togglePass.bind(this);
        this.handlePassTypeChange = this.handlePassTypeChange.bind(this);
        this.handlePassNumberChange = this.handlePassNumberChange.bind(this);

        this.passholderTypeId = 'memberPassholderType';
        this.passholderId = 'memberPassholderId';

        this.state = {
            showPasses: get(this.props, 'alwaysDisplayPassOptions', false),
            currentSelection: ADULT_YOUTH_PASS.toString(),
        };
    }

    // If we ever start pulling options async for some reason, add fees rebounce outside didMount
    componentDidMount() {
        // if (this.props.member.passholderId !== "" && this.props.member.passholderId !== ADULT_YOUTH_PASS) {
        //   this.togglePass();
        // }

        // Defaults to Adult/Youth if present, else defaults to Adult
        let defaultSelection = this.props.options[0]
            ? ADULT_YOUTH_PASS.toString()
            : ADULT_PASS.toString();
        if (this.props.packCreek) {
            defaultSelection = NON_COMMERCIAL_PASS.toString();
        }
        const currentSelection = !this.props.member.passholderType
            ? defaultSelection
            : this.props.member.passholderType.toString();
        this.setState({
            currentSelection,
        });
        // Bounce passtype to reset fees after component loaded because 0 is no longer guaranteed default
        this.handlePassTypeChange({ value: currentSelection, id: this.passholderTypeId });
    }

    static getDerivedStateFromProps(props) {
        if (props.alwaysDisplayPassOptions) {
            return {
                showPasses: true,
            };
        }
        return null;
    }

    togglePass(passholderTypeId, passholderId) {
        if (!this.props.alwaysDisplayPassOptions) {
            this.setState((oldState) => ({
                showPasses: !oldState.showPasses,
            }));
            this.props.handleMemberInfoChange(
                this.props.index,
                passholderTypeId,
                ADULT_YOUTH_PASS
            );
            this.props.handleMemberInfoChange(this.props.index, passholderId, '');
            this.props.formatPackCreekFeeDescription(1);
        }
    }

    handlePassTypeChange({ value, id }) {
        const v = parseInt(value, 10);
        this.setState({
            currentSelection: value,
        });
        this.props.handleMemberInfoChange(this.props.index, id, v);
        if (this.props.packCreek) {
            this.props.formatPackCreekFeeDescription(value);
        }
    }

    handlePassNumberChange(e) {
        const value = e.target.value;
        const id = e.target.id;

        this.props.handleMemberInfoChange(this.props.index, id, value);
    }

    render() {
        if (!this.state.showPasses && !this.props.alwaysDisplayPassOptions) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        type="button"
                        size="sm"
                        appearance="secondary"
                        onClick={this.togglePass}
                        className="applyPassButton"
                        iconBeforeElement={<Icon iconName="add-circle-outline" />}
                    >
                        Apply Pass
                    </Button>
                </div>
            );
        }

        const disabled =
            this.state.currentSelection === ADULT_YOUTH_PASS.toString() ||
            this.state.currentSelection === ADULT_PASS.toString() ||
            this.state.currentSelection === YOUTH_PASS.toString() ||
            this.props.packCreek;
        // alwaysDisplay sites are adult/youth, so fees rather than passes
        const label = !this.props.alwaysDisplayPassOptions ? 'Pass Type' : 'Fee Type';
        return (
            <div>
                <FlexRow>
                    <FlexCol md={4}>
                        <div className="rec-form-item-wrap">
                            <Select
                                label={label}
                                id={`${this.passholderTypeId}-${this.props.index}`}
                                className="applyPassDropdown"
                                value={this.state.currentSelection}
                                onChange={(e) =>
                                    this.handlePassTypeChange({
                                        value: e.target.value,
                                        id: this.passholderTypeId,
                                    })
                                }
                                options={Object.keys(this.props.options)
                                    .filter((key) => key !== '0')
                                    .map((option) => ({
                                        value: option,
                                        label: this.props.options[option],
                                    }))}
                            />
                        </div>
                    </FlexCol>
                    {!disabled && (
                        <FlexCol md={4}>
                            <div className="rec-form-item-wrap">
                                <TextField
                                    label="Pass ID#"
                                    className="passIdInput"
                                    id={this.passholderId}
                                    isDisabled={disabled}
                                    type="text"
                                    value={this.props.member.passholderId || ''}
                                    onChange={this.handlePassNumberChange}
                                />
                            </div>
                        </FlexCol>
                    )}
                    {!this.props.alwaysDisplayPassOptions && (
                        <FlexCol md={4} alignSelf="end">
                            <div className="per-form-group-item-remove">
                                <Button
                                    appearance="link"
                                    type="button"
                                    iconBeforeElement={<Icon iconName="remove-circle" />}
                                    onClick={() =>
                                        this.togglePass(
                                            this.passholderTypeId,
                                            this.passholderId
                                        )
                                    }
                                >
                                    Remove Pass
                                </Button>
                            </div>
                        </FlexCol>
                    )}
                </FlexRow>
                {this.state.currentSelection !== ADULT_YOUTH_PASS.toString() &&
                    this.state.currentSelection !== ADULT_PASS.toString() &&
                    this.state.currentSelection !== YOUTH_PASS.toString() &&
                    !this.props.alwaysDisplayPassOptions && (
                        <p>
                            <em>
                                Note: Present the above pass when obtaining the permit.
                                Otherwise the discount will be removed and you will be
                                charged the full amount.
                            </em>
                        </p>
                    )}
            </div>
        );
    }
}
